import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Điện trường đều (Lớp 11 — Bài 12): hai bản kim loại NẰM NGANG, bản + ở trên, bản − ở dưới.
// E hướng từ bản + (trên) xuống bản − (dưới). Điện tích thử chịu lực F = qE → chuyển động như
// "ném" trong trường đều: v₀ dọc theo E → quỹ đạo THẲNG; v₀ xiên → quỹ đạo PARABOL.
const PX0 = 64, PX1 = 560, TOP = 46, BOT = 286
const START_X = PX0 + 30, START_Y = (TOP + BOT) / 2

const ElectricFieldSimulation: React.FC<Props> = ({
  title = 'Điện trường đều giữa hai bản',
  description = 'Bản + ở trên, bản − ở dưới; E⃗ hướng xuống. Điện tích chịu lực F = qE. Chỉnh hướng v₀: dọc E → quỹ đạo thẳng; xiên → parabol.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const p = useRef({ x: START_X, y: START_Y })
  const v = useRef({ x: 0, y: 0 })
  const trail = useRef<{ x: number; y: number }[]>([])
  const [E, setE] = useState(3)
  const [q, setQ] = useState(2)
  const [v0, setV0] = useState(140)
  const [angle, setAngle] = useState(0) // độ, 0 = ngang phải; + = chếch xuống; ±90 = thẳng đứng
  const [running, setRunning] = useState(false)
  const [, force] = useState(0)
  const firstRef = useRef(false)
  const W = 600, H = 330

  const reset = useCallback(() => {
    setRunning(false)
    p.current = { x: START_X, y: START_Y }; v.current = { x: 0, y: 0 }; trail.current = []
    force((n) => n + 1)
  }, [])
  useEffect(() => { reset() }, [reset])

  // mũi tên tiện ích
  const arrow = (ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, color: string, lw = 3) => {
    const len = Math.hypot(dx, dy); if (len < 2) return
    const ex = x + dx, ey = y + dy, a = Math.atan2(dy, dx)
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = lw; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(ex - 11 * Math.cos(a - 0.42), ey - 11 * Math.sin(a - 0.42)); ctx.lineTo(ex - 11 * Math.cos(a + 0.42), ey - 11 * Math.sin(a + 0.42)); ctx.closePath(); ctx.fill()
    ctx.lineCap = 'butt'
  }
  // nhãn vектơ: chữ + mũi tên nhỏ phía trên
  const vecLabel = (ctx: CanvasRenderingContext2D, txt: string, x: number, y: number, color: string) => {
    ctx.fillStyle = color; ctx.font = 'bold italic 16px sans-serif'; ctx.textAlign = 'left'
    const w = ctx.measureText(txt).width
    ctx.fillText(txt, x, y)
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(x - 1, y - 15); ctx.lineTo(x + w + 1, y - 15); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(x + w + 1, y - 15); ctx.lineTo(x + w - 3, y - 18); ctx.lineTo(x + w - 3, y - 12); ctx.closePath(); ctx.fill()
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    // hai bản: + (đỏ, trên), − (xanh, dưới)
    ctx.fillStyle = '#ef4444'; ctx.fillRect(PX0, TOP - 6, PX1 - PX0, 10)
    ctx.fillStyle = '#3b82f6'; ctx.fillRect(PX0, BOT - 4, PX1 - PX0, 10)
    ctx.fillStyle = '#b91c1c'; ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'left'
    for (let x = PX0 + 18; x < PX1; x += 46) ctx.fillText('+', x, TOP - 10)
    ctx.fillStyle = '#1d4ed8'
    for (let x = PX0 + 18; x < PX1; x += 46) ctx.fillText('−', x, BOT + 22)

    // đường sức E (xuống), MŨI TÊN gần bản âm (dưới)
    ctx.strokeStyle = 'rgba(124,58,237,0.45)'; ctx.fillStyle = 'rgba(124,58,237,0.45)'; ctx.lineWidth = 1.5
    for (let x = PX0 + 40; x < PX1 - 20; x += 70) {
      ctx.beginPath(); ctx.moveTo(x, TOP + 8); ctx.lineTo(x, BOT - 8); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(x, BOT - 8); ctx.lineTo(x - 5, BOT - 18); ctx.lineTo(x + 5, BOT - 18); ctx.closePath(); ctx.fill()
    }
    vecLabel(ctx, 'E', PX0 + 8, (TOP + BOT) / 2, '#7c3aed')

    // quỹ đạo (vết)
    if (trail.current.length > 1) {
      ctx.strokeStyle = 'rgba(220,38,38,0.5)'; ctx.lineWidth = 2; ctx.beginPath()
      trail.current.forEach((pt, i) => i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y))
      ctx.stroke()
    }

    const { x, y } = p.current
    // vектơ v₀ (lúc chưa chạy) — xanh lá
    if (!running && trail.current.length === 0) {
      const rad = (angle * Math.PI) / 180
      arrow(ctx, x, y, Math.cos(rad) * (30 + v0 * 0.18), Math.sin(rad) * (30 + v0 * 0.18), '#16a34a')
      vecLabel(ctx, 'v₀', x + 6, y - 18, '#16a34a')
    }
    // vектơ lực F = qE (q>0 → xuống bản âm; q<0 → lên)
    const F = q * E
    const fl = Math.sign(F) * Math.min(70, 16 + Math.abs(F) * 7)
    if (Math.abs(fl) > 2) { arrow(ctx, x, y, 0, fl, '#db2777'); vecLabel(ctx, 'F', x + 10, y + fl + (fl > 0 ? 16 : -4), '#db2777') }
    // điện tích thử
    const g = ctx.createRadialGradient(x - 5, y - 5, 2, x, y, 13)
    g.addColorStop(0, '#ffffff'); g.addColorStop(0.4, q >= 0 ? '#f87171' : '#60a5fa'); g.addColorStop(1, q >= 0 ? '#b91c1c' : '#1d4ed8')
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 13, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = '#fff'; ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(q >= 0 ? '+' : '−', x, y + 5); ctx.textAlign = 'left'
  }, [E, q, v0, angle, running])

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.04, (ts - lastRef.current) / 1000); lastRef.current = ts
    const ay = q * E * 16 // a = F/m theo phương E (xuống dương nếu q>0)
    v.current.y += ay * dt
    p.current.x += v.current.x * dt; p.current.y += v.current.y * dt
    trail.current.push({ x: p.current.x, y: p.current.y })
    let stop = false
    if (p.current.y >= BOT - 13) { p.current.y = BOT - 13; stop = true }
    if (p.current.y <= TOP + 13) { p.current.y = TOP + 13; stop = true }
    if (p.current.x >= PX1 || p.current.x <= PX0) stop = true
    force((n) => n + 1); draw()
    if (stop) { setRunning(false); return }
    raf.current = requestAnimationFrame(tick)
  }, [q, E, draw])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { draw() }, [draw])

  const launch = () => {
    const rad = (angle * Math.PI) / 180
    // bắn TỪ VỊ TRÍ HIỆN TẠI của điện tích (đã kéo đặt), không reset về điểm mặc định
    v.current = { x: Math.cos(rad) * v0, y: Math.sin(rad) * v0 }
    trail.current = []
    setRunning(true)
  }

  // ── Kéo điện tích đặt vào vị trí bất kì (khi chưa bắn) ──
  const dragRef = useRef(false)
  const toLogic = (e: React.PointerEvent) => { const r = cv.current!.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width * W, y: (e.clientY - r.top) / r.height * H } }
  const onDown = (e: React.PointerEvent) => {
    if (running) return
    const m = toLogic(e)
    if (Math.hypot(m.x - p.current.x, m.y - p.current.y) < 22) {
      dragRef.current = true; trail.current = []; (e.target as HTMLElement).setPointerCapture?.(e.pointerId)
      if (!firstRef.current) { firstRef.current = true; onComplete?.() }
    }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const m = toLogic(e)
    p.current = { x: Math.max(PX0 + 14, Math.min(PX1 - 14, m.x)), y: Math.max(TOP + 14, Math.min(BOT - 14, m.y)) }
    v.current = { x: 0, y: 0 }; force((n) => n + 1); draw()
  }
  const onUp = () => { dragRef.current = false }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Vận tốc v₀</span><input type="range" min={0} max={260} step={10} value={v0} onChange={(e) => setV0(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{v0}</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Hướng v₀</span><input type="range" min={-90} max={90} step={5} value={angle} onChange={(e) => setAngle(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{angle}°</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Cường độ E</span><input type="range" min={0} max={6} step={1} value={E} onChange={(e) => setE(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{E}</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Điện tích q</span><input type="range" min={-3} max={3} step={1} value={q} onChange={(e) => setQ(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{q > 0 ? '+' : ''}{q}</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Mẹo: hướng v₀ = ±90° (dọc theo E) → quỹ đạo THẲNG; v₀ ngang/xiên → PARABOL. q=0 → đi thẳng đều.</p>
      <div className="flex gap-2 mt-2">
        <button onClick={launch} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700"><Play className="w-4 h-4" /> Bắn điện tích</button>
        <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default ElectricFieldSimulation
